import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import {
  BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, LabelList,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import { Loader2, Timer, Target, AlertTriangle } from "lucide-react";
import ReportFilterBar from "@/components/ops/ReportFilterBar";
import LoadingOverlay from "@/components/common/LoadingOverlay";
import { useSortableTable, SortableTh } from "@/lib/useSortableTable";

const CHANNEL_COLORS = { call: "#0A84FF", sms: "#22C55E", wn: "#F59E0B", invalid: "#EF4444" };

const fmt = (n) => (n === null || n === undefined ? "—" : Number(n).toLocaleString());
const fmtPct = (n) => (n === null || n === undefined ? "—" : `${Number(n).toFixed(1)}%`);
const fmtDays = (n) => (n === null || n === undefined ? "—" : `${Number(n).toFixed(1)}d`);

function Kpi({ label, value, sub, icon: Icon, tone = "default", testId }) {
  const tones = {
    default: "bg-white/70 text-slate-700",
    good: "bg-emerald-100/70 text-emerald-700",
    warn: "bg-amber-100/70 text-amber-700",
  };
  return (
    <div className="glass-card p-5" data-testid={testId}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-wide text-slate-500 font-medium">{label}</div>
          <div className="kpi-num text-3xl mt-1 text-slate-900">{value}</div>
          {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
        </div>
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${tones[tone]}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
}

export default function TimeToConvertPage() {
  const [params, setParams] = useState({});
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get("/ops/reports/time-to-convert", { params })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [params]);

  const bins = data?.bins || [];
  const chartData = bins.map((b) => ({ name: b.label, count: b.count, pct: b.pct }));
  const p = data?.percentiles || {};
  const sla = data?.sla || {};

  const channelPieData = useMemo(
    () => (data?.channels || []).filter((c) => c.count > 0).map((c) => ({ name: c.channel, value: c.count })),
    [data]
  );

  const { rows: campaignsSorted, thProps: cthProps } = useSortableTable(
    data?.campaigns || [],
    { initialKey: "conversions", initialDir: "desc",
      accessors: { campaign_name: (r) => (r.campaign_name || "").toLowerCase() } }
  );

  const { rows: channelsSorted, thProps: chthProps } = useSortableTable(
    data?.channels || [],
    { initialKey: "count", initialDir: "desc",
      accessors: { channel: (r) => (r.channel || "").toLowerCase() } }
  );

  return (
    <div className="space-y-6" data-testid="ttc-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-outfit font-semibold text-3xl md:text-4xl text-slate-900 tracking-tight">
            Time to convert
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Days between a lead&apos;s first call attempt and their deposit — validates the 10-day allowed window.
          </p>
        </div>
        <ReportFilterBar onChange={setParams} testIdPrefix="ttc" />
      </div>

      <LoadingOverlay loading={loading} testId="ttc-loading">
        {/* KPI row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Kpi
            label="Total conversions"
            value={fmt(data?.total_conversions)}
            sub="Users converted (call · sms · wn · invalid)"
            icon={Target}
            testId="ttc-kpi-total"
          />
          <Kpi
            label="Within 10-day window"
            value={fmt(sla.within_10_days)}
            sub={`${fmtPct(sla.within_10_days_pct)} of conversions`}
            icon={Target}
            tone="good"
            testId="ttc-kpi-sla"
          />
          <Kpi
            label="Median (P50)"
            value={fmtDays(p.p50)}
            sub={`P90 ${fmtDays(p.p90)} · P95 ${fmtDays(p.p95)}`}
            icon={Timer}
            testId="ttc-kpi-p50"
          />
          <Kpi
            label="Outside 10-day"
            value={fmt(sla.outside_10_days)}
            sub={data?.outside_window_anomalies ? `+${data.outside_window_anomalies} anomaly (pre-window)` : "Deposits past the campaign window"}
            icon={AlertTriangle}
            tone={sla.outside_10_days ? "warn" : "default"}
            testId="ttc-kpi-outside"
          />
        </div>

        {/* Histogram */}
        <div className="glass-card p-5 mt-8" data-testid="ttc-histogram">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-2">
            Distribution of days from first call → conversion
          </div>
          {chartData.length === 0 || data?.total_conversions === 0 ? (
            <div className="text-sm text-slate-500 py-8 text-center">No conversions in the selected window.</div>
          ) : (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 24, right: 20, left: 0, bottom: 6 }}>
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip
                    formatter={(v, k) => (k === "count" ? [`${fmt(v)} users`, "Users"] : [v, k])}
                    labelStyle={{ color: "#334155" }}
                  />
                  <Bar dataKey="count" fill="#0A84FF" radius={[8, 8, 0, 0]}>
                    <LabelList dataKey="pct" position="top"
                      formatter={(v) => (v > 0 ? `${Number(v).toFixed(0)}%` : "")}
                      style={{ fontSize: 10, fill: "#475569" }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Channels + Campaigns two-column grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
          <div className="glass-card p-5" data-testid="ttc-channels">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-2">
              By credit channel
            </div>
            {channelPieData.length > 0 && (
              <div className="h-40 mb-2">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={channelPieData} dataKey="value" nameKey="name" cx="35%" cy="50%"
                         innerRadius={35} outerRadius={65} paddingAngle={2}>
                      {channelPieData.map((entry) => (
                        <Cell key={entry.name} fill={CHANNEL_COLORS[entry.name] || "#94A3B8"} />
                      ))}
                    </Pie>
                    <Legend layout="vertical" verticalAlign="middle" align="right"
                            wrapperStyle={{ fontSize: 11, textTransform: "capitalize" }} />
                    <Tooltip formatter={(v) => [`${fmt(v)} users`, "Users"]} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
            <table className="w-full text-sm">
              <thead data-sticky>
                <tr className="text-left text-xs uppercase border-b border-slate-100">
                  <SortableTh {...chthProps("channel", "left")}>Channel</SortableTh>
                  <SortableTh {...chthProps("count")}>Users</SortableTh>
                  <SortableTh {...chthProps("pct")}>Share</SortableTh>
                  <SortableTh {...chthProps("avg")}>Avg</SortableTh>
                  <SortableTh {...chthProps("p50")}>P50</SortableTh>
                  <SortableTh {...chthProps("p90")}>P90</SortableTh>
                </tr>
              </thead>
              <tbody>
                {channelsSorted.map((c) => (
                  <tr key={c.channel} className="border-b border-slate-50/70">
                    <td className="py-2 px-2 capitalize font-medium text-slate-800">
                      <span className="inline-block w-2 h-2 rounded-full mr-2"
                            style={{ background: CHANNEL_COLORS[c.channel] || "#94A3B8" }} />
                      {c.channel}
                    </td>
                    <td className="py-2 px-2 text-right tabular-nums">{fmt(c.count)}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{fmtPct(c.pct)}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{fmtDays(c.avg)}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{fmtDays(c.p50)}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{fmtDays(c.p90)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="glass-card p-5" data-testid="ttc-campaigns">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-2">
              Campaign SLA compliance (top 30)
            </div>
            <div className="overflow-auto max-h-96">
              <table className="w-full text-sm">
                <thead data-sticky>
                  <tr className="text-left text-xs uppercase border-b border-slate-100">
                    <SortableTh {...cthProps("campaign_name", "left")}>Campaign</SortableTh>
                    <SortableTh {...cthProps("conversions")}>Users</SortableTh>
                    <SortableTh {...cthProps("avg_days")}>Avg d</SortableTh>
                    <SortableTh {...cthProps("within_10_days_pct")}>≤10d</SortableTh>
                  </tr>
                </thead>
                <tbody>
                  {campaignsSorted.map((c) => (
                    <tr key={c.campaign_id} className="border-b border-slate-50/70">
                      <td className="py-2 pr-2 px-2">
                        <div className="font-medium text-slate-800 truncate max-w-[220px]">{c.campaign_name}</div>
                        <div className="text-[10px] text-slate-500">{c.brand_name}</div>
                      </td>
                      <td className="py-2 px-2 text-right tabular-nums">{fmt(c.conversions)}</td>
                      <td className="py-2 px-2 text-right tabular-nums">{fmtDays(c.avg_days)}</td>
                      <td className="py-2 px-2 text-right">
                        <span className={`px-1.5 py-0.5 rounded ${c.within_10_days_pct >= 90 ? "bg-emerald-50 text-emerald-700" : c.within_10_days_pct >= 70 ? "bg-amber-50 text-amber-700" : "bg-red-50 text-red-700"}`}>
                          {fmtPct(c.within_10_days_pct)}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {campaignsSorted.length === 0 && (
                    <tr><td colSpan={4} className="py-4 text-center text-slate-500">No data.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </LoadingOverlay>
    </div>
  );
}
